import React, { ReactNode } from 'react'
import { makeStyles } from 'tss-react/mui'
import { Typography } from '@mui/material'

import { CatalogImage } from '@src/types/graphql-types'
import { ProductDetailFragment } from '@src/fragments/ProductDetail.generated'
import HandleMarkup from '@src/components/HandleMarkup'

const useStyles = makeStyles()((theme) => ({
  mainContent: {
    display: 'flex',
  },
  stackedAndCentered: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    textAlign: 'center',
  },
  mainContentImage: {
    height: 60,
    width: 60,
    flexShrink: 0,
    borderRadius: 4,
    border: `1px solid ${theme.palette.grey[400]}`,
    textAlign: 'center',
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  },
  image: {
    // required for ie11 styling
    position: 'relative',
    top: '50%',
    transform: 'translateY(-50%)',
    maxHeight: '100%',
    maxWidth: '100%',
    width: 'auto',
    height: 'auto',
  },
  mainContentText: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    marginLeft: theme.spacing(4.5),
  },
  productNumber: {
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightBold,
    lineHeight: 1.5,
    textTransform: 'uppercase',
  },
  productName: {
    maxHeight: 66,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.5,
    '& sup, sub': {
      verticalAlign: 'baseline',
      position: 'relative',
      top: '-0.4em',
    },
    '& sub': {
      top: '0.1em',
    },
  },
  productBrand: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.25,
  },
  clampLines: {
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
    overflow: 'hidden',
    wordBreak: 'break-word',
    textOverflow: 'ellipsis',
  },
  mainContentHeight: {
    height: 120,
  },
  vr1: {
    marginBottom: theme.spacing(1),
  },
  vr2: {
    marginBottom: theme.spacing(2),
  },
}))

export interface NewPdpProductLockupMiniProps {
  productNumber: ProductDetailFragment['productNumber']
  name: ProductDetailFragment['name']
  description: ProductDetailFragment['description']
  image?: Pick<CatalogImage, 'mediumUrl' | 'altText'>
  displayDiscount?: (discountPercent?: string | null) => ReactNode
  discount?: string | null
}

export const NewPdpProductLockupMini = ({
  productNumber,
  name,
  description,
  image,
  displayDiscount = undefined,
  discount,
}: NewPdpProductLockupMiniProps) => {
  const displayProductNumber = !productNumber?.startsWith('CB_')
  const { classes, cx } = useStyles()

  return (
    <div
      className={cx(classes.mainContent, {
        [classes.mainContentHeight]: !!discount,
      })}
    >
      {image && (
        <div className={classes.mainContentImage}>
          <img
            className={classes.image}
            src={image.mediumUrl}
            alt={image.altText || description || name}
            width="112"
            height="50"
          />
        </div>
      )}
      <div className={cx([classes.mainContentText])}>
        {displayProductNumber && (
          <Typography className={classes.productNumber}>
            {productNumber}
          </Typography>
        )}
        <Typography className={cx(classes.productName, classes.clampLines)}>
          <HandleMarkup value={name} />
        </Typography>
        {discount && displayDiscount && displayDiscount(discount)}
      </div>
    </div>
  )
}
